<template>
  <form class="search__form" @submit.prevent="validateBeforeSubmit">
    <fieldset class="border-none p-0 flex flex-col">
      <div
        class="grid grid-rows md:grid-cols-4 gap-4 mb-4"
        :class="[isSearchMethod(searchMethods.postalcode) ? 'md:grid-cols-6' : 'md:grid-cols-4']"
        @submit="validateBeforeSubmit"
      >
        <div class="search-selector relative w-full">
          <label class="mb-2">Zoeken op</label>
          <m-select
            id="searchOptions"
            name="searchOptions"
            label=""
            :value="activeSearchMethod"
            :options="searchOptions"
            @change-option="setSearchMethod"
          />
        </div>
        <div id="step1" class="">
          <v-input-date
            id="dateOfBirth"
            ref="dateOfBirth"
            type="date"
            name="dateOfBirth"
            label="Geboortedatum"
            @update-validation="(value) => (isDateOfBirthValidated = value)"
            @input="nextInputCheck"
            :rules="{
              date: true
            }"
          />
        </div>
        <div v-if="isSearchMethod(searchMethods.policynumber)" class="">
          <v-input-PolicyNumber
            id="policynumber"
            v-model="policynumber"
            ref="policynumber"
            label="Polisnummer"
            @update-validation="(value) => (isPolicyNumberValidated = value)"
            @input="nextInputCheck"
            :rules="{
              required: true,
              numeric: true
            }"
          />
        </div>
        <div v-if="isSearchMethod(searchMethods.bsn)" id="step2" class="">
          <v-input-BSN
            id="bsn"
            ref="bsn"
            v-model="bsnNumber"
            name="bsn"
            label="BSN"
            :rules="{
              required: true,
              min: 9,
              max: 9,
              isBsn: true
            }"
            @update-validation="(value) => (isBSNValidated = value)"
            @input="nextInputCheck"
          />
        </div>
        <div v-if="isSearchMethod(searchMethods.postalcode)" class="">
          <v-input-postalcode
            id="postalcode"
            ref="postalcode"
            v-model="postalcode"
            name="postalcode"
            label="Postcode"
            :rules="{
              required: true,
              regex: /^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-z]{2}$/i
            }"
            @update-validation="(value) => (isPostalcodeValidated = value)"
            @input="nextInputCheck"
          />
        </div>
        <div v-if="isSearchMethod(searchMethods.postalcode)" class="">
          <v-input-housenumber
            ref="housenumber"
            id="housenumber"
            v-model="housenumber"
            name="housenumber"
            label="Huisnr."
            :rules="{
              required: true,
              numeric: true
            }"
            @update-validation="(value) => (isHousenumberValidated = value)"
            @input="nextInputCheck"
          />
        </div>
        <div v-if="isSearchMethod(searchMethods.postalcode)" class="">
          <v-input-group-simple
            label="Toev."
            inputid="housenumberAddition"
            placeholder="C"
            :rule="{ required: false }"
          />
        </div>
        <div class="mt-0 md:mt-7">
          <button
            id="step3"
            class="flex justify-center py-2 px-2 md:px-4 shadow-sm text-sm font-medium text-white border border-primary-default bg-primary-default rounded-full hover:bg-primary-default/50 items-center mb-2 mr-2 mt-0 md:mt-2"
          >
            <span class="">Zoek</span>
            <MagnifyingGlassIcon
              class="h-3 md:h-5 lg:h-5 w-3 md:w-5 lg:w-5 inline-block text-white ml-2"
              aria-hidden="true"
            />
          </button>
        </div>
        <div class="mt-0">
          <button
            v-if="showCreateNewPatientButton"
            @click.prevent="createNewPatient"
            class="flex justify-center py-2 px-2 md:px-4 shadow-sm text-sm font-medium text-pdsPrimaryColor border border-primary-default bg-white rounded-full hover:bg-primary-default/50 items-center mb-2 mr-2 mt-0 md:mt-2"
          >
            <span class="">Nieuwe patiënt aanmelden</span>
            <ArrowRightIcon
              class="h-3 md:h-5 lg:h-5 w-3 md:w-5 lg:w-5 inline-block text-pdsPrimaryColor ml-2"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </fieldset>
    <!--Pop up to show Suggention for search -->
    <generic-modal v-if="showPopUp">
      <template #header> Let op! </template>
      <template #body>
        <p>
          Zoeken op BSN geeft betere resultaten. Wanneer 'Zoek op postcode' geen resultaat geeft, raden wij u aan om
          alsnog een patiënt te selecteren via 'Zoek op BSN'.
        </p>
      </template>
      <template #footer>
        <v-button tagelement="button" hollow md primary transparent @click.prevent="setSearchMethod(searchMethods.bsn)">
          Zoek op BSN
        </v-button>
        <v-button tagelement="button" md primary @click.prevent="closeModal"> Zoek op postcode </v-button>
      </template>
    </generic-modal>
  </form>
</template>

<script>
import { useStore, mapActions } from 'vuex';
import { computed, ref } from 'vue';
import VInputDate from '@/components/shared/form/simple/InputDate';
import VInputBSN from '@/components/shared/form/simple/InputBSN';
import VInputPostalcode from '@/components/shared/form/simple/InputPostalcode';
import VInputHousenumber from '@/components/shared/form/simple/InputHousenumber';
import VInputPolicyNumber from '@/components/shared/form/simple/InputPolicyNumber';
import GenericModal from '@/components/shared/GenericModal';
import router from '@/router';
import { useForm } from 'vee-validate';
import { MagnifyingGlassIcon, ArrowRightIcon } from '@heroicons/vue/24/outline';

export default {
  name: 'VSearchForm',
  components: {
    VInputDate,
    VInputBSN,
    VInputPostalcode,
    VInputHousenumber,
    VInputPolicyNumber,
    GenericModal,
    MagnifyingGlassIcon,
    ArrowRightIcon
  },
  props: {
    showCreateNewPatientButton: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const store = useStore();
    const isSearchEnabled = ref(false);
    const isPostalcodeActive = computed(() => store.getters['search/getIsPostalcodeActive']);
    const results = computed(() => store.getters['search/getResults']);
    const setNoPatientFound = (value) => store.commit('search/setNoPatientFound', value);
    const setIsPostalcodeActive = (value) => store.commit('search/setIsPostalcodeActive', value);
    const setShowOneChildStatus = (value) => store.commit('navigation/setShowOneChildStatus', value);
    const multiplePatientFound = computed(() => store.getters['search/getMultiplePatientsFound']);
    const resetMultiplePatientFound = (value) => store.commit('search/setMultiplePatientsFound', false);

    return {
      setShowOneChildStatus,
      isPostalcodeActive,
      results,
      setNoPatientFound,
      setIsPostalcodeActive,
      isSearchEnabled,
      ...mapActions('patient', ['setEndpoints', 'setActivePatient']),
      multiplePatientFound,
      resetMultiplePatientFound
    };
  },
  data() {
    const { validate } = useForm();
    var searchMethods = {
      bsn: 'BSN',
      policynumber: 'Polisnummer',
      postalcode: 'Postcode'
    };

    return {
      validate,
      searchMethods: searchMethods,
      activeSearchMethod: searchMethods.bsn,
      isDateOfBirthValidated: false,
      isBSNValidated: false,
      isPostalcodeValidated: false,
      isHousenumberValidated: false,
      isPolicyNumberValidated: false,
      showPopUp: false,
      searchOptions: ['BSN', 'Polisnummer', 'Postcode'],
      housenumber: null,
      policynumber: null,
      bsnNumber: null,
      postalcode: null
    };
  },
  watch: {
    multiplePatientFound(value) {
      if (value) {
        this.setSearchMethod(this.searchMethods.bsn);
      }
    }
  },
  created() {
    this.setSearchMethod(this.searchMethods.bsn);
  },
  unmounted() {
    this.setIsPostalcodeActive(true);
  },
  methods: {
    createNewPatient() {
      this.setEndpoints('new').then(() => {
        this.setShowOneChildStatus(false);
        router.push('/patientgegevens');
      });
    },
    closeModal() {
      this.showPopUp = false;
    },
    async validateBeforeSubmit(e) {
      e.preventDefault();
      this.resetMultiplePatientFound(false);
      const formValid = await this.isFormValid();
      if (formValid) {
        this.$emit('formValidated', e.currentTarget.elements);
      }
    },
    setSearchMethod(searchMethod) {
      this.isSearchEnabled = false;
      if (this.activeSearchMethod === searchMethod) {
        return;
      }
      this.setNoPatientFound(false);
      this.activeSearchMethod = searchMethod;
      var isPostalcodeActive = this.activeSearchMethod === this.searchMethods.postalcode;
      this.setIsPostalcodeActive(isPostalcodeActive);
      this.showPopUp = isPostalcodeActive;
    },
    isSearchMethod(searchMethod) {
      return this.activeSearchMethod === searchMethod;
    },
    async nextInputCheck() {
      if (this.isSearchMethod(this.searchMethods.bsn)) {
        this.isSearchEnabled = this.isBSNValidated && this.isDateOfBirthValidated;
        return this.isSearchEnabled;
      } else if (this.isSearchMethod(this.searchMethods.postalcode)) {
        return (this.isSearchEnabled =
          this.isDateOfBirthValidated && this.isPostalcodeValidated && this.isHousenumberValidated);
      } else if (this.isSearchMethod(this.searchMethods.policynumber)) {
        return (this.isSearchEnabled = this.isDateOfBirthValidated && this.isPolicyNumberValidated);
      }
      return false;
    },
    async isFormValid() {
      const result = await this.validate();
      if (result.valid) {
        return true;
      } else {
        return false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.search__form {
  .form-group-simple,
  .form-group-simple--completed {
    :deep(input) {
      color: $teal-darkest !important;
      height: 49px;
    }
    :deep(label) {
      color: $teal-darkest;
      margin-bottom: 8px;
    }
  }
  .search-selector {
    :deep(.material-design-icon) {
      display: none;
    }
    .form-group {
      padding: 0;
      width: 100%;
      :deep(.form-group__details) {
        &:after {
          content: none;
        }
        &:before {
          content: none;
        }
      }
      :deep(select) {
        -webkit-appearance: none;
        -moz-appearance: none;
        background-image: url("data:image/svg+xml;utf8,<svg fill='gray' height='24' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/><path d='M0 0h24v24H0z' fill='none'/></svg>");
        background-repeat: no-repeat;
        background-position-x: 100%;
        background-position-y: 50%;
        min-height: 50px;
        line-height: 1.5rem;
        color: $teal-darkest;
        padding-left: 10px;
        border: solid 1px;
        border-color: $gray;
        border-radius: 3px;
        transition: opacity 0.2s ease-in-out;
        box-sizing: border-box;
      }
    }
  }
  .btn {
    @include max-screen(grid-breakpoint(md)) {
      @include btn-lq;
    }
  }
  fieldset {
    label {
      font-size: type-scale(0);
      color: $teal-darkest;
      font-weight: $form-group-simple-label-font-weight;
      margin-bottom: 8px;
      line-height: normal;
    }
  }
}
</style>
